import { createContext, useContext, useCallback, useState } from 'react';
import { useApolloClient, useQuery } from '@apollo/client';
import { ME } from '../graphql/adminQueries';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const client = useApolloClient();
  // After an unauthenticated ME (GraphQL error), writeQuery alone may not update
  // useQuery observers — keep a session overlay until ME refetch succeeds.
  const [sessionUser, setSessionUser] = useState(undefined);
  const { data, loading: loadingMe, refetch } = useQuery(ME, {
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  const networkUser = data?.me ?? null;
  const user = sessionUser !== undefined ? sessionUser : networkUser;
  const loadingUser = sessionUser === undefined && loadingMe;

  const setUser = useCallback(
    (nextUser) => {
      setSessionUser(nextUser);
      client.cache.writeQuery({ query: ME, data: { me: nextUser } });
      if (nextUser) {
        // Refresh ME with cookies; drop overlay once network agrees.
        refetch()
          .then((result) => {
            if (result.data?.me?.id === nextUser.id) {
              setSessionUser(undefined);
            }
          })
          .catch(() => {
            /* keep session overlay on transient ME failure */
          });
      }
    },
    [client, refetch]
  );

  const logout = async () => {
    const apiUrl = process.env.REACT_APP_API_URL || '';
    const origin = apiUrl.replace(/\/graphql\/?$/i, '');
    if (origin) {
      try {
        await fetch(`${origin}/auth/logout`, { method: 'POST', credentials: 'include' });
      } catch {
        /* network unavailable */
      }
    }
    setUser(null);
  };

  const value = {
    user,
    setUser,
    loadingUser,
    isAdmin: String(user?.role).toUpperCase() === 'ADMIN',
    isStaff: String(user?.role).toUpperCase() === 'STAFF',
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
